import { useContext } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import {
  GenerateSaleExportContext,
  GenerationSourceCreatedContext,
} from '@/lib/contexts/generate-sale-export.context';
import { clearDealItemsInProgressToast } from '@/lib/deal-items-toast';
import {
  DatasourceInstructionMode,
  DatasourceInstructionUpdateField,
  ShopifyCollection,
  ShopifyProductStatus,
  AddShopifyDataSourceToDealDocument,
  GetShopifyCollectionsDocument,
  UpdateDealItemsV2Document,
} from '@/lib/graphql/generated/graphql';
import { useDealNotEditableHandler } from '@/lib/hooks/useDealNotEditableHandler';
import { trackEvent } from '@/lib/mixpanel';
import { cn } from '@/lib/utils';
import { showDomainErrorToast } from '@/lib/utils/domain-error-translator';
import { productsState } from '@/states/products.state';
import { useMutation, useQuery } from '@apollo/client/react';
import { useAtomValue } from 'jotai';
import { ArrowLeft } from 'lucide-react';

import type { BaseImportViewProps } from './import-view-props';
import {
  ExcludedItemsConfirmationDialog,
  type ExcludedMatchItem,
} from './excluded-items-confirmation-dialog';
import { ShopifyBulkProgress } from './shopify-bulk-progress';

const parseInputToArray = (input: string): string[] => {
  if (!input.trim()) return [];
  return input
    .split(/[\n,\s]+/)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
};

const CHUNK_SIZE = 250;
const WITHOUT_COLLECTION_ID = '__no_collections__';

interface RefChunk {
  skus: string[];
  gtins: string[];
}

const buildChunks = (skus: string[], gtins: string[]): RefChunk[] => {
  const tagged = [
    ...skus.map((s) => ({ type: 'sku' as const, value: s })),
    ...gtins.map((g) => ({ type: 'gtin' as const, value: g })),
  ];
  const result: RefChunk[] = [];
  for (let i = 0; i < tagged.length; i += CHUNK_SIZE) {
    const chunk = tagged.slice(i, i + CHUNK_SIZE);
    result.push({
      skus: chunk.filter((r) => r.type === 'sku').map((r) => r.value),
      gtins: chunk.filter((r) => r.type === 'gtin').map((r) => r.value),
    });
  }
  return result;
};

const AVAILABLE_STATUSES = [
  ShopifyProductStatus.Active,
  ShopifyProductStatus.Archived,
  ShopifyProductStatus.Unlisted,
  ShopifyProductStatus.Draft,
];

const UPDATE_FIELDS = [
  { field: DatasourceInstructionUpdateField.Name, key: 'field_name' },
  {
    field: DatasourceInstructionUpdateField.Description,
    key: 'field_description',
  },
  { field: DatasourceInstructionUpdateField.Options, key: 'field_options' },
  { field: DatasourceInstructionUpdateField.Images, key: 'field_images' },
  {
    field: DatasourceInstructionUpdateField.ListPrice,
    key: 'field_list_price',
  },
  {
    field: DatasourceInstructionUpdateField.DiscountedPrice,
    key: 'field_discounted_price',
  },
  {
    field: DatasourceInstructionUpdateField.StockQuantity,
    key: 'field_stock_quantity',
  },
  {
    field: DatasourceInstructionUpdateField.ProductCategory,
    key: 'field_product_category',
  },
  {
    field: DatasourceInstructionUpdateField.SourceUrl,
    key: 'field_source_url',
  },
] as const;

interface ViewProductsShopifyProps extends BaseImportViewProps {
  dealId: string;
  instructionMode?: DatasourceInstructionMode;
  onModifyOption?: () => void;
  onCloseDialog?: () => void;
}

type FilterMode = 'collections' | 'skus-gtins';

export const ViewProductsShopify: React.FC<ViewProductsShopifyProps> = ({
  setImportView,
  dealId,
  instructionMode,
  onCloseDialog,
}) => {
  const { t } = useTranslation();
  const generateSaleExport = useContext(GenerateSaleExportContext);
  const onSourceCreated = useContext(GenerationSourceCreatedContext);

  const { toast } = useToast();
  const progressToastTrackerIdRef = useRef<string | null>(null);

  const clearProgressToast = () => {
    if (!progressToastTrackerIdRef.current) return;
    clearDealItemsInProgressToast({
      trackerId: progressToastTrackerIdRef.current,
    });
    progressToastTrackerIdRef.current = null;
  };

  // State for filter mode (collections or SKUs/GTINs)
  const [filterMode, setFilterMode] = useState<FilterMode>('collections');

  // State for selected collections
  const [selectedCollections, setSelectedCollections] = useState<string[]>([]);
  const [withoutCollection, setWithoutCollection] = useState(false);

  // State for SKUs and GTINs input
  const [skusInput, setSkusInput] = useState<string>('');
  const [gtinsInput, setGtinsInput] = useState<string>('');
  const [isCompleted, setIsCompleted] = useState(false);
  const [hasBulkError, setHasBulkError] = useState(false);
  const [objectCount, setObjectCount] = useState(0);
  // State for selected product statuses (default to ACTIVE)
  const [selectedStatuses, setSelectedStatuses] = useState<
    ShopifyProductStatus[]
  >([ShopifyProductStatus.Active]);

  // State for selected fields to update (default to empty - user chooses)
  const [selectedFields, setSelectedFields] = useState<
    DatasourceInstructionUpdateField[]
  >([]);

  // State for excluded items confirmation dialog
  const [excludedDialogOpen, setExcludedDialogOpen] = useState(false);
  const [matchingExcludedItems, setMatchingExcludedItems] = useState<
    ExcludedMatchItem[]
  >([]);

  // Read existing deal items from Jotai atom (populated by parent page)
  const products = useAtomValue(productsState);

  // Mutation for re-including excluded items
  const [reincludeMutation, { loading: reincluding }] = useMutation(
    UpdateDealItemsV2Document
  );

  const { data, loading, error } = useQuery(GetShopifyCollectionsDocument, {
    variables: {
      dealId,
      status: selectedStatuses,
    },
    fetchPolicy: 'network-only',
    skip: filterMode === 'skus-gtins',
  });

  const { handleIfDealNotEditable } = useDealNotEditableHandler();

  const [startShopifySyncMutation, { loading: syncing }] = useMutation(
    AddShopifyDataSourceToDealDocument
  );

  // Bulk operation progress tracking
  const [pollingActive, setPollingActive] = useState(false);
  const [generationSourceId, setGenerationSourceId] = useState<string>();

  // Chunked batch processing (refs for stable access in async callbacks)
  const chunksRef = useRef<RefChunk[]>([]);
  const currentChunkIndexRef = useRef(0);
  const accumulatedCountRef = useRef(0);
  const [progressKey, setProgressKey] = useState(0);
  const [waitingNextChunk, setWaitingNextChunk] = useState(false);

  const parsedSkus = useMemo(() => parseInputToArray(skusInput), [skusInput]);
  const parsedGtins = useMemo(
    () => parseInputToArray(gtinsInput),
    [gtinsInput]
  );
  const totalInputCount = parsedSkus.length + parsedGtins.length;
  const isReady =
    !loading && !error && !syncing && !pollingActive && !waitingNextChunk;
  const isUpdateOnly = instructionMode === DatasourceInstructionMode.UpdateOnly;

  const findExcludedMatchingItems = (
    skus: string[],
    gtins: string[]
  ): ExcludedMatchItem[] => {
    const skuSet = new Set(skus.map((s) => s.toLowerCase()));
    const gtinSet = new Set(gtins.map((g) => g.toLowerCase()));

    return products.reduce<ExcludedMatchItem[]>((acc, p) => {
      const av = p.activeVersion;
      if (av.isSelected) return acc;

      if (av.sku && skuSet.has(av.sku.toLowerCase())) {
        acc.push({
          id: p.id,
          name: av.name ?? '',
          matchedRef: av.sku,
          matchType: 'sku',
        });
      } else if (av.gtin && gtinSet.has(av.gtin.toLowerCase())) {
        acc.push({
          id: p.id,
          name: av.name ?? '',
          matchedRef: av.gtin,
          matchType: 'gtin',
        });
      } else if (
        av.shopifyVariantId &&
        skuSet.has(av.shopifyVariantId.toLowerCase())
      ) {
        acc.push({
          id: p.id,
          name: av.name ?? '',
          matchedRef: av.shopifyVariantId,
          matchType: 'shopifyVariantId',
        });
      }
      return acc;
    }, []);
  };

  const startShopifySync = React.useCallback(
    (
      collections: { id: string; name: string }[],
      status: ShopifyProductStatus[],
      skus: string[],
      gtins: string[],
      generateForSource = generateSaleExport
    ) => {
      // Determine if we're using collections or SKUs/GTINs
      const useCollections = filterMode === 'collections';

      return startShopifySyncMutation({
        variables: {
          ...(generateForSource && { generateSaleExport: true }),
          dealId,
          collections:
            useCollections && !isUpdateOnly ? collections : undefined,
          instructionMode,
          status: !isUpdateOnly ? status : undefined,
          fields:
            isUpdateOnly && selectedFields.length > 0
              ? selectedFields
              : undefined,
          // Send SKUs/GTINs only if in that mode and not empty
          skus: !useCollections && skus.length > 0 ? skus : undefined,
          gtins: !useCollections && gtins.length > 0 ? gtins : undefined,
        },
      });
    },
    [
      dealId,
      startShopifySyncMutation,
      instructionMode,
      isUpdateOnly,
      selectedFields,
      filterMode,
      generateSaleExport,
    ]
  );

  useEffect(() => {
    if (
      data?.getShopifyCollections?.__typename === 'ShopifyCollectionSuccess'
    ) {
      setSelectedCollections(
        data.getShopifyCollections.collections
          .filter((c) => c.selected)
          .map((c) => c.id)
      );
    }
  }, [data?.getShopifyCollections]);

  // Toggle collection selection
  const toggleCollection = (collectionId: string) => {
    setSelectedCollections((prev) =>
      prev.includes(collectionId)
        ? prev.filter((id) => id !== collectionId)
        : [...prev, collectionId]
    );
  };

  // Toggle status selection
  const toggleStatus = (status: ShopifyProductStatus) => {
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  // Toggle field selection
  const toggleField = (field: DatasourceInstructionUpdateField) => {
    setSelectedFields((prev) =>
      prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]
    );
  };

  // Select all collections
  const selectAll = () => {
    if (
      data?.getShopifyCollections?.__typename === 'ShopifyCollectionSuccess'
    ) {
      setSelectedCollections(
        data.getShopifyCollections.collections.map((c) => c.id)
      );
      setWithoutCollection(true);
    }
  };

  // Deselect all collections
  const deselectAll = () => {
    setSelectedCollections([]);
    setWithoutCollection(false);
  };

  const getCollections = () => {
    if (
      data?.getShopifyCollections?.__typename === 'ShopifyCollectionSuccess'
    ) {
      return data.getShopifyCollections.collections
        .filter((c) => selectedCollections.includes(c.id))
        .map((c) => ({ id: c.id, name: c.name }));
    }
    return [];
  };

  const executeChunkRef = useRef<(index: number) => Promise<void>>();

  const executeChunk = async (index: number) => {
    const chunk = chunksRef.current[index];
    // Only the last chunk triggers the offer file generation.
    const generatesFile =
      generateSaleExport && index === chunksRef.current.length - 1;
    try {
      const result = await startShopifySync(
        [],
        selectedStatuses,
        chunk.skus,
        chunk.gtins,
        generatesFile
      );

      const mutationResult = result.data?.addShopifyDataSourceToDeal;

      if (mutationResult?.__typename === 'DomainException') {
        setWaitingNextChunk(false);
        clearProgressToast();
        if (handleIfDealNotEditable(mutationResult.code)) return;
        showDomainErrorToast({
          toast,
          t,
          code: mutationResult.code,
          titleKey: 'general.error_shopify',
          defaultMessageKey: 'general.error_shopify_message',
        });
        return;
      }

      if (mutationResult?.__typename === 'MutationSuccess') {
        if (generateSaleExport) setGenerationSourceId(mutationResult.id);
        if (generatesFile) onSourceCreated?.(mutationResult.id);
        setWaitingNextChunk(false);
        setProgressKey((prev) => prev + 1);
        setPollingActive(true);
        return;
      }

      setWaitingNextChunk(false);
      clearProgressToast();
      toast({
        title: t('general.error_shopify'),
        description: t('general.error_shopify_message'),
        variant: 'destructive',
      });
    } catch (err) {
      setWaitingNextChunk(false);
      clearProgressToast();
      console.error('Error during chunk execution:', err);
      toast({
        title: t('general.error_shopify'),
        description: t('general.error_shopify_message'),
        variant: 'destructive',
      });
    }
  };
  executeChunkRef.current = executeChunk;

  const handleChunkCompleted = React.useCallback((chunkObjectCount: number) => {
    const newAccumulated = accumulatedCountRef.current + chunkObjectCount;
    accumulatedCountRef.current = newAccumulated;
    setPollingActive(false);

    const nextIndex = currentChunkIndexRef.current + 1;
    currentChunkIndexRef.current = nextIndex;

    if (nextIndex < chunksRef.current.length) {
      setWaitingNextChunk(true);
      setTimeout(() => executeChunkRef.current?.(nextIndex), 1000);
    } else {
      setIsCompleted(true);
      setObjectCount(newAccumulated);
    }
  }, []);

  const handleChunkError = React.useCallback(() => {
    setPollingActive(false);
    setHasBulkError(true);
  }, []);

  const executeSyncFlow = async () => {
    trackEvent('IMPORT_STARTED', { dealId, source: 'shopify' });
    const trackFailure = (errorCode: string, step?: string) => {
      trackEvent('IMPORT_FAILED', {
        dealId,
        source: 'shopify',
        errorCode,
        step,
      });
    };

    // For skus-gtins mode, chunk and execute sequentially
    if (filterMode === 'skus-gtins') {
      const skus = parsedSkus;
      const gtins = parsedGtins;
      const chunks = buildChunks(skus, gtins);
      chunksRef.current = chunks;
      currentChunkIndexRef.current = 0;
      accumulatedCountRef.current = 0;
      await executeChunk(0);
      return;
    }

    // Collections mode: a full selection is equivalent to no Shopify filter.
    try {
      const selectedShopifyCollections = getCollections();
      const importsAllProducts =
        data?.getShopifyCollections?.__typename ===
          'ShopifyCollectionSuccess' &&
        selectedShopifyCollections.length ===
          data.getShopifyCollections.collections.length &&
        withoutCollection;
      const collectionsToImport = importsAllProducts
        ? []
        : [
            ...selectedShopifyCollections,
            ...(withoutCollection
              ? [
                  {
                    id: WITHOUT_COLLECTION_ID,
                    name: t(
                      'components.import_flow.shopify.without_collection'
                    ),
                  },
                ]
              : []),
          ];
      const result = await startShopifySync(
        collectionsToImport,
        selectedStatuses,
        [],
        []
      );

      const mutationResult = result.data?.addShopifyDataSourceToDeal;

      if (mutationResult?.__typename === 'DomainException') {
        clearProgressToast();
        trackFailure(mutationResult.code, 'addShopifyDataSource');
        if (handleIfDealNotEditable(mutationResult.code)) return;
        showDomainErrorToast({
          toast,
          t,
          code: mutationResult.code,
          titleKey: 'general.error_shopify',
          defaultMessageKey: 'general.error_shopify_message',
        });
        return;
      }

      if (mutationResult?.__typename === 'MutationSuccess') {
        if (generateSaleExport) {
          setGenerationSourceId(mutationResult.id);
          onSourceCreated?.(mutationResult.id);
        }
        trackEvent('IMPORT_COMPLETED', {
          dealId,
          source: 'shopify',
        });
        setPollingActive(true);
        return;
      }

      clearProgressToast();
      trackFailure('UNKNOWN', 'addShopifyDataSource');
      toast({
        title: t('general.error_shopify'),
        description: t('general.error_shopify_message'),
        variant: 'destructive',
      });
    } catch (err) {
      clearProgressToast();
      console.error('Error during creation:', err);
      trackFailure(err instanceof Error ? err.message : 'unknown_error');
      toast({
        title: t('general.error_shopify'),
        description: t('general.error_shopify_message'),
        variant: 'destructive',
      });
    }
  };

  const handleSubmit = async () => {
    // Validation: ensure at least one filter is provided
    if (
      filterMode === 'collections' &&
      selectedCollections.length === 0 &&
      !withoutCollection &&
      !isUpdateOnly
    ) {
      toast({
        title: t('general.error'),
        description: t(
          'components.import_flow.shopify.error_no_collections_selected'
        ),
        variant: 'destructive',
      });
      return;
    }

    if (filterMode === 'skus-gtins') {
      if (parsedSkus.length === 0 && parsedGtins.length === 0) {
        toast({
          title: t('general.error'),
          description: t('components.import_flow.shopify.error_no_skus_gtins'),
          variant: 'destructive',
        });
        return;
      }

      // Check for excluded items matching the entered SKUs/GTINs
      const excluded = findExcludedMatchingItems(parsedSkus, parsedGtins);
      if (excluded.length > 0) {
        setMatchingExcludedItems(excluded);
        setExcludedDialogOpen(true);
        return;
      }
    }

    await executeSyncFlow();
  };

  const handleIncludeAndImport = async () => {
    try {
      const result = await reincludeMutation({
        variables: {
          dealId,
          items: matchingExcludedItems.map((item) => ({
            id: item.id,
            isSelected: true,
          })),
        },
      });

      if (result.data?.updateDealItemsV2?.__typename === 'DomainException') {
        if (handleIfDealNotEditable(result.data.updateDealItemsV2.code)) {
          return;
        }
        toast({
          title: t('general.error'),
          description: t(
            'components.import_flow.shopify.error_reinclude_failed'
          ),
          variant: 'destructive',
        });
        return;
      }

      setExcludedDialogOpen(false);
      await executeSyncFlow();
    } catch (err) {
      console.error('Error re-including items:', err);
      toast({
        title: t('general.error'),
        description: t('components.import_flow.shopify.error_reinclude_failed'),
        variant: 'destructive',
      });
    }
  };

  const handleContinueWithout = async () => {
    setExcludedDialogOpen(false);
    await executeSyncFlow();
  };

  const collections =
    data?.getShopifyCollections?.__typename === 'ShopifyCollectionSuccess'
      ? data.getShopifyCollections.collections
      : [];

  const allCollectionsSelected =
    selectedCollections.length === collections.length && withoutCollection;

  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-2">
          <button
            className="rounded-md p-1 hover:bg-muted disabled:opacity-50"
            onClick={() => setImportView('productsShopifyOptionSelection')}
            aria-label={t('components.import_flow.back_to_options')}
            disabled={pollingActive}
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <DialogTitle>{t('components.import_flow.shopify.title')}</DialogTitle>
        </div>
      </DialogHeader>

      {isCompleted && (
        <div className="flex h-[400px] items-center justify-center">
          <p className="mt-2 text-sm font-medium text-green-900">
            {t('components.import_flow.shopify.completed', {
              count: objectCount,
            })}
          </p>
        </div>
      )}

      {hasBulkError && (
        <div className="flex h-[400px] items-center justify-center">
          <p className="mt-2 text-sm font-medium text-destructive">
            {t('general.error_shopify_message')}
          </p>
        </div>
      )}

      {(loading || syncing) && !isCompleted && !waitingNextChunk && (
        <div className="flex h-[400px] items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
        </div>
      )}

      {error && (
        <div className="flex h-[400px] items-center justify-center">
          <p className="mt-2 text-sm text-red-600">{error.message}</p>
        </div>
      )}

      {/* Bulk operation progress — single block for polling + between-chunk waiting */}
      {(pollingActive || waitingNextChunk) &&
        (pollingActive ? (
          <ShopifyBulkProgress
            key={progressKey}
            dealId={dealId}
            dataSourceId={generationSourceId}
            onCompleted={
              chunksRef.current.length > 1
                ? handleChunkCompleted
                : (count) => {
                    setPollingActive(false);
                    setIsCompleted(true);
                    setObjectCount(count);
                  }
            }
            onError={handleChunkError}
          />
        ) : (
          <div className="flex h-[400px] flex-col items-center justify-center gap-4">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            <div className="text-center">
              <p className="text-sm font-medium">
                {t('general.shopify_import_in_progress')}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('general.shopify_import_chunk_progress', {
                  current: currentChunkIndexRef.current,
                  total: chunksRef.current.length,
                })}
              </p>
            </div>
          </div>
        ))}

      {/* Filter Mode Selector */}
      {isReady && !isUpdateOnly && !isCompleted && (
        <div className="flex gap-2 border-b px-1 pb-4">
          <Button
            variant={filterMode === 'collections' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterMode('collections')}
          >
            {t('components.import_flow.shopify.filter_by_collections')}
          </Button>
          <Button
            variant={filterMode === 'skus-gtins' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterMode('skus-gtins')}
          >
            {t('components.import_flow.shopify.filter_by_skus_gtins')}
          </Button>
        </div>
      )}

      {/* Scrollable content area - Collections Mode */}
      {isReady &&
        !isUpdateOnly &&
        filterMode === 'collections' &&
        !isCompleted && (
          <ScrollArea className="h-[500px]">
            <div className="pr-4">
              {/* Product Status Filter */}
              <div className="space-y-4 border-b pb-6 pt-2">
                <div className="px-1">
                  <p className="mb-1 text-sm font-medium">
                    {t('components.import_flow.shopify.product_status')}
                  </p>
                </div>
                <TooltipProvider>
                  <div className="flex flex-wrap gap-6 px-1">
                    {AVAILABLE_STATUSES.map((status) => {
                      const isDisabled = status === ShopifyProductStatus.Draft;

                      const option = (
                        <label
                          key={status}
                          className={cn(
                            'flex items-center gap-2.5',
                            isDisabled
                              ? 'cursor-not-allowed opacity-50'
                              : 'cursor-pointer'
                          )}
                        >
                          <Checkbox
                            checked={selectedStatuses.includes(status)}
                            onCheckedChange={() => toggleStatus(status)}
                            disabled={isDisabled}
                            className={cn(isDisabled && 'pointer-events-none')}
                          />
                          <span className="text-sm">
                            {t(
                              `components.import_flow.shopify.status_${status.toLowerCase()}`
                            )}
                          </span>
                        </label>
                      );

                      return isDisabled ? (
                        <Tooltip key={status}>
                          <TooltipTrigger asChild>{option}</TooltipTrigger>
                          <TooltipContent className="z-[80]">
                            {t(
                              'components.import_flow.shopify.status_draft_disabled_tooltip'
                            )}
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        option
                      );
                    })}
                  </div>
                </TooltipProvider>
              </div>

              {/* Collections list */}
              <div className="flex flex-col gap-4 py-4">
                {/* Selection controls */}
                <div className="flex items-center justify-between px-1">
                  <p className="text-sm text-muted-foreground">
                    {t('components.import_flow.shopify.selected', {
                      count:
                        selectedCollections.length +
                        (withoutCollection ? 1 : 0),
                    })}{' '}
                    / {collections.length + 1}
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={allCollectionsSelected ? deselectAll : selectAll}
                  >
                    {t(
                      allCollectionsSelected
                        ? 'general.deselect_all'
                        : 'general.select_all'
                    )}
                  </Button>
                </div>

                {/* Collections */}
                <div className="space-y-2 px-1">
                  <WithoutCollectionItem
                    isSelected={withoutCollection}
                    onToggle={() => setWithoutCollection((value) => !value)}
                  />
                  {collections.map((collection) => (
                    <CollectionItem
                      key={collection.id}
                      collection={collection}
                      isSelected={selectedCollections.includes(collection.id)}
                      onToggle={() => toggleCollection(collection.id)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </ScrollArea>
        )}

      {/* Scrollable content area - SKUs/GTINs Mode */}
      {isReady &&
        !isUpdateOnly &&
        filterMode === 'skus-gtins' &&
        !isCompleted && (
          <ScrollArea className="h-[500px]">
            <div className="space-y-6 pr-4 pt-2">
              {/* SKUs Input */}
              <div className="space-y-2 px-1">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="mb-1 text-sm font-medium">
                      {t('components.import_flow.shopify.skus_label')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('components.import_flow.shopify.skus_description')}
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {parsedSkus.length} SKUs
                  </span>
                </div>
                <Textarea
                  value={skusInput}
                  onChange={(e) => setSkusInput(e.target.value)}
                  placeholder={t(
                    'components.import_flow.shopify.skus_placeholder'
                  )}
                  className="min-h-[120px] font-mono text-sm"
                />
              </div>

              {/* GTINs Input */}
              <div className="space-y-2 px-1">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="mb-1 text-sm font-medium">
                      {t('components.import_flow.shopify.gtins_label')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('components.import_flow.shopify.gtins_description')}
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {parsedGtins.length} GTINs
                  </span>
                </div>
                <Textarea
                  value={gtinsInput}
                  onChange={(e) => setGtinsInput(e.target.value)}
                  placeholder={t(
                    'components.import_flow.shopify.gtins_placeholder'
                  )}
                  className="min-h-[120px] font-mono text-sm"
                />
              </div>

              {/* Counter and Info message */}
              <div className="space-y-3 px-1">
                <div className="rounded-md border border-muted bg-muted p-3">
                  <p className="text-xs font-medium text-muted-foreground">
                    {t('components.import_flow.shopify.total_count', {
                      count: totalInputCount,
                    })}
                  </p>
                </div>
                <div className="rounded-md bg-muted p-3">
                  <p className="text-xs text-muted-foreground">
                    {t('components.import_flow.shopify.skus_gtins_info')}
                  </p>
                </div>
              </div>
            </div>
          </ScrollArea>
        )}

      {/* Fields to Update Filter */}
      {isReady && isUpdateOnly && !isCompleted ? (
        <ScrollArea className="h-[400px]">
          <div className="space-y-4 border-b pb-6 pt-2">
            <div className="px-1">
              <p className="mb-1 text-sm font-medium">
                {t('components.import_flow.shopify.fields_to_update')}
              </p>
              <p className="text-xs text-muted-foreground">
                {t(
                  'components.import_flow.shopify.fields_to_update_description'
                )}
              </p>
            </div>
            <div className="flex flex-wrap gap-6 px-1">
              {UPDATE_FIELDS.map(({ field, key }) => (
                <label
                  key={field}
                  className="flex cursor-pointer items-center gap-2.5"
                >
                  <Checkbox
                    checked={selectedFields.includes(field)}
                    onCheckedChange={() => toggleField(field)}
                  />
                  <span className="text-sm">
                    {t(`components.import_flow.shopify.${key}`)}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </ScrollArea>
      ) : null}

      {/* Footer with action buttons */}
      {isReady && !isCompleted && (
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setImportView('productsShopifyOptionSelection')}
          >
            {t('general.previous')}
          </Button>
          <Button onClick={handleSubmit}>
            {t('general.importation_confirm')}
          </Button>
        </DialogFooter>
      )}

      {(isCompleted || hasBulkError) && (
        <DialogFooter>
          <Button variant="outline" onClick={onCloseDialog}>
            {t('general.close')}
          </Button>
        </DialogFooter>
      )}

      {!isCompleted && (
        <ExcludedItemsConfirmationDialog
          open={excludedDialogOpen}
          onOpenChange={setExcludedDialogOpen}
          excludedItems={matchingExcludedItems}
          onIncludeAndImport={handleIncludeAndImport}
          onContinueWithout={handleContinueWithout}
          isLoading={reincluding}
        />
      )}
    </>
  );
};

// Collection item component
interface CollectionItemProps {
  collection: ShopifyCollection;
  isSelected: boolean;
  onToggle: () => void;
}

const CollectionItem: React.FC<CollectionItemProps> = ({
  collection,
  isSelected,
  onToggle,
}) => {
  return (
    <div
      className={cn(
        'flex cursor-pointer items-center gap-3 rounded-lg border-2 p-4 transition-all',
        'hover:border-accent hover:bg-accent',
        isSelected
          ? 'border-primary bg-primary/5'
          : 'border-border bg-background',
        collection.productsFilteredCount === 0 ? 'cursor-not-allowed' : ''
      )}
      onClick={collection.productsFilteredCount === 0 ? undefined : onToggle}
    >
      <Checkbox
        checked={isSelected}
        onCheckedChange={onToggle}
        disabled={collection.productsFilteredCount === 0}
        onClick={(e) => e.stopPropagation()}
      />
      <div className="flex-1">
        <p className="text-sm font-medium">{collection.name}</p>
        <p className="text-xs text-muted-foreground">
          {collection.productsFilteredCount}/{collection.productsCount}{' '}
          {collection.productsFilteredCount === 1 ? 'product' : 'products'}
        </p>
      </div>
    </div>
  );
};

interface WithoutCollectionItemProps {
  isSelected: boolean;
  onToggle: () => void;
}

const WithoutCollectionItem: React.FC<WithoutCollectionItemProps> = ({
  isSelected,
  onToggle,
}) => {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        'flex cursor-pointer items-center gap-3 rounded-lg border-2 p-4 transition-all',
        'hover:border-accent hover:bg-accent',
        isSelected
          ? 'border-primary bg-primary/5'
          : 'border-border bg-background'
      )}
      onClick={onToggle}
    >
      <Checkbox
        checked={isSelected}
        onCheckedChange={onToggle}
        onClick={(event) => event.stopPropagation()}
      />
      <div className="flex-1">
        <p className="text-sm font-medium">
          {t('components.import_flow.shopify.without_collection')}
        </p>
        <p className="text-xs text-muted-foreground">
          {t('components.import_flow.shopify.without_collection_description')}
        </p>
      </div>
    </div>
  );
};
